import {
  ELEMENT_PARAGRAPH,
  ELEMENT_TABLE,
  ELEMENT_TD,
  type HotkeyPlugin,
  Plate,
  type PlatePlugin,
  type TEditableProps,
  type TEditor,
  type TNode,
  createComboboxPlugin,
  createNodeIdPlugin,
  createParagraphPlugin,
  createPlateUI,
  createPluginFactory,
  createPlugins,
  getNextNode,
  getNode,
  insertNodes,
  removeNodes,
  withPlaceholders,
} from "@udecode/plate";
import React from "react";

import { v4 as uuidv4 } from "uuid";

import { createCopyPastePlugin } from "@/common/editor/components/copyPaste/copyPaste";
import clsx from "clsx";
import i18next from "i18next";
import { type BaseEditor, type BaseElement, Editor, Transforms } from "slate";
import { ELEMENT_RULE, Rule, createOperatorPlugin, createRulePlugin } from "../components";
import { TableCell, TableElem, createDecisionTablesPlugin } from "../components/decisionTable";
import { createConnectorPlugin } from "../components/operator/connector";
import { paragraphEnter, paragraphShiftEnter, paragraphTabbing } from "../components/paragraph/plugins";
import { createRuleBreakPlugin } from "../components/ruleBreak";
import { useSyntaxHighlighter } from "../components/syntax";
import { EditorActiveContext } from "../components/syntax/EditorActiveContext";
import {
  AttributeCombobox,
  EnumerationCombobox,
  FunctionCombobox,
  RelationshipCombobox,
  createAttributesPlugin,
  createEnumerationsPlugin,
  createFunctionsPlugin,
  createRelationshipsPlugin,
} from "../plugins/mentions";
import { type HotkeysPlugin, withHotKeys } from "../plugins/util";
import { EditorState, ToolbarStateBridge } from "./multiEditor";
import { createSectionAwareExitBreakPlugin } from "./ruleEditorExitBreak";
import { createRuleEditorFullTableDeletePlugin } from "./ruleEditorFullTableDelete";

// don't need to define these here, but if we want to use withHOC they need to be here
let components = createPlateUI({
  [ELEMENT_RULE]: Rule,
  [ELEMENT_TD]: TableCell,
  [ELEMENT_TABLE]: TableElem,
});

// withDraggables(..., draggableOptions)
// withDebugPaths(..., {})

// plugin to ensure rules all have ids
const createRuleIdPlugin = createPluginFactory({
  key: "rule-id",
  normalizeInitialValue: (initialValue) => {
    // recursively iterate over initialValue and add a id using uuid to each node of type rule
    const addId = (node: any) => {
      if (node.type === ELEMENT_RULE) {
        node.id = uuidv4();
      }
      if (node.children) {
        node.children.forEach(addId);
      }
    };
    addId({ children: initialValue });
    return initialValue;
  },
  withOverrides: (editor) => {
    const { normalizeNode } = editor;
    editor.normalizeNode = ([node, path]) => {
      normalizeNode([node, path]);
      const normalizedNode = getNode(editor, path);
      if (normalizedNode?.type === ELEMENT_RULE) {
        if (!normalizedNode.id) {
          normalizedNode.id = uuidv4();
          Transforms.setNodes<TNode>(editor as BaseEditor, { ...normalizedNode }, { at: path });
        }
      }
      // remove annoying unicode characters
      if (normalizedNode && typeof normalizedNode.text === "string") {
        normalizedNode.text = normalizedNode.text
          //.replace(/[\u2018\u2019]/g, "'")
          // fancy dashes
          .replace(/[\u2011\u2012\u2013\u2014]/g, "-");
      }
    };
    return editor;
  },
});

const createEnsureValidDocumentPlugin = createPluginFactory({
  key: "ensure-valid-document",
  withOverrides: (editor) => {
    const { normalizeNode } = editor;
    editor.normalizeNode = ([node, path]) => {
      normalizeNode([node, path]);
      // ensure document always has at least one paragraph
      if (path.length === 0) {
        if (editor.children.length === 0) {
          const p = { type: ELEMENT_PARAGRAPH, children: [{ text: "" }] };
          insertNodes(editor, p, { at: [0], select: true });
        }
        // shouldn't need to do this, but keeping in case we want it
        // if (editor.children.length === 1) {
        //   const first = editor.children[0];
        //   if (first.type !== ELEMENT_PARAGRAPH && first.type !== ELEMENT_RULE && first.type !== ELEMENT_TABLE) {
        //     const p = { type: ELEMENT_PARAGRAPH, children: [{ text: "" }] };
        //     insertNodes(editor, p, { at: [0], select: true });
        //   }
        // }
      }
    };
    return editor;
  },
});

export const createRulePlugins = (plugins: PlatePlugin[] = []) => {
  //const { t } = useTranslation();
  // We have to call i18next direclty here as this isn't a hook
  components = withPlaceholders(components, [
    { key: ELEMENT_PARAGRAPH, placeholder: i18next.t("documents.enter_text"), hideOnBlur: true },
    { key: ELEMENT_RULE, placeholder: i18next.t("documents.enter_condition"), hideOnBlur: false },
  ]);

  return createPlugins(
    [
      createRuleIdPlugin(),
      createNodeIdPlugin({ options: { idKey: "id", idCreator: uuidv4, allow: [ELEMENT_RULE] } }),
      // ensure its before the rules, as its an override that needs to run first
      createRuleBreakPlugin(),
      createOperatorPlugin(),
      createConnectorPlugin(),
      createRulePlugin(),
      // ensure paragraph comes after rules, as we want it as a fallback
      // @ts-ignore
      createParagraphPlugin<HotkeyPlugin & HotkeysPlugin>({
        ...withHotKeys([paragraphTabbing, paragraphEnter, paragraphShiftEnter]),
        withOverrides: (editor) => {
          const { deleteForward } = editor;
          // an override function to allow custom delete
          // NOTE this is necessary in order to allow deleting empty paragraphs above rules/sections
          editor.deleteForward = (unit) => {
            const { selection } = editor;
            if (!selection) return deleteForward(unit);
            const basePath = selection.focus.path.slice(0, -1);
            const parentNode = getNode(editor, basePath);
            // only do our special case if we are in a paragraph
            if (!parentNode || parentNode.type !== ELEMENT_PARAGRAPH) {
              return deleteForward(unit);
            }
            const next = getNextNode(editor, { at: basePath });
            if (next) {
              const [node, path] = next;
              const isEmptyParagraph = Editor.isEmpty(editor as BaseEditor, parentNode as BaseElement);
              // if the next node is not a paragraph, do our special case and delete the paragraph instead
              // only do this if the current paragraph is empty
              if (node.type !== ELEMENT_PARAGRAPH && isEmptyParagraph) {
                removeNodes(editor as TEditor, { at: basePath });
                return;
              }
            }
            deleteForward(unit);
          };
          return editor;
        },
      }),
      createSectionAwareExitBreakPlugin(),
      createComboboxPlugin(),
      createFunctionsPlugin(),
      createAttributesPlugin(),
      createRelationshipsPlugin(),
      createEnumerationsPlugin(),
      createDecisionTablesPlugin(),
      // put it at the end as a fallback
      createCopyPastePlugin(),
      createEnsureValidDocumentPlugin(),
      ...plugins,
      createRuleEditorFullTableDeletePlugin(),
    ],
    {
      components,
    },
  );
};

export type EditorProps = React.PropsWithChildren & {
  id: string;
  editableProps?: TEditableProps;
};

export const RuleEditor = ({ id, editableProps, children }: EditorProps) => {
  const { decorate, renderLeaf: syntaxRenderLeaf } = useSyntaxHighlighter(id);

  // Wrap renderLeaf to add highlighting for AI-generated changes
  const renderLeaf = React.useCallback(
    (props: any) => {
      const { leaf, children, attributes } = props;

      // Check for our custom highlight mark
      if (leaf.highlight) {
        return (
          <span
            {...attributes}
            style={{
              backgroundColor: "#d4edda",
              padding: "2px 4px",
              borderRadius: "3px",
            }}
          >
            {children}
          </span>
        );
      }

      // Fallback to syntax highlighter
      return syntaxRenderLeaf ? syntaxRenderLeaf(props) : <span {...attributes}>{children}</span>;
    },
    [syntaxRenderLeaf],
  );
  const [active, setActive] = React.useState(false);

  const editProps = {
    ...editableProps,
    decorate,
    className: clsx(editableProps?.className),
    // TODO what was this trying to achieve???
    // ISSUE: using this blocks cursor selection and makes authoring feel awful
    // onMouseDownCapture: () => {
    //   setActive(true);
    // },
    // onMouseUpCapture: () => {
    //   setActive(false);
    // },
    scrollSelectionIntoView: (editor, domRange) => {
      // disable autoscroll to selection
      // is annoying when we are working with 2 editors and large documents
      // also solves insert section scrolling back to top issue
      return;
    },
    renderLeaf,
  };

  return (
    <EditorActiveContext.Provider value={active}>
      <Plate
        id={id}
        editableProps={editProps}
      >
        <ToolbarStateBridge editor={EditorState.rules} />
        <FunctionCombobox />
        <AttributeCombobox />
        <RelationshipCombobox />
        <EnumerationCombobox />
        {children}
      </Plate>
    </EditorActiveContext.Provider>
  );
};
